import React, { useState, useEffect } from 'react';
import AdsCard from '../AdsCard';
import facebook_ads_icon from '../../../../assests/facebook.png';
import { toast } from 'react-toastify';
import { getFacebookAdAccounts, storeFacebookIntegrationCredentials } from '../../../../services/ApiService';
import {
  clearFacebookOAuthState,
  clearOAuthCallbackParams,
  getFacebookAccountType,
  getOAuthRedirectUri,
} from '../../../../utils/oauthRedirect';
import { getIntegrationErrorMessage } from '../../../../utils/oauthErrors';
import { FacebookAdsAccountTable } from './FacebookAdsAccountTable';
import { CommonModal } from '../../../CommonModal';
import { ConfirmModal } from '../../../ConfirmModal';
import { useNavigate } from 'react-router-dom';

const facebookOAuthDevLog = (...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.log('[Facebook OAuth Dev]', ...args);
  }
};

const FacebookAdsIntegartion = ({
  isAccountLinked,
  fetchAdsAccountStatus,
  accountDetails,
  social_media_type,
}) => {
  const navigate = useNavigate();
  const expectedAccountType = getFacebookAccountType(social_media_type);

  const [connecting, setConnecting] = useState(false);
  const [adAccounts, setAdAccounts] = useState([]);
  const [showAdsAccountsModal, setShowAdsAccountsModal] = useState(false);
  const [confirmAdsAccountModal, setConfirmAdsAccountModal] = useState(false);

  const platform = social_media_type === 'organic_social' ? 'Facebook' : 'Facebook Ads';

  const finishOAuthCallback = () => {
    clearFacebookOAuthState();
    clearOAuthCallbackParams(navigate);
  };

  const resetFacebookConnect = () => {
    setConnecting(false);
    setConfirmAdsAccountModal(false);
    clearFacebookOAuthState();
  };

  const handleConnect = async () => {
    setConnecting(true);
    setConfirmAdsAccountModal(false);
    localStorage.setItem('selected_platform_to_connect', 'facebook');
    localStorage.setItem('account_type', expectedAccountType);
    localStorage.removeItem('facebook_code');

    facebookOAuthDevLog('Connect confirmed', {
      redirect_uri: getOAuthRedirectUri(),
      account_type: expectedAccountType,
    });

    try {
      const result = await storeFacebookIntegrationCredentials({
        account_type: expectedAccountType,
      });

      if (!result?.success) {
        toast.error(getIntegrationErrorMessage(result, 'Failed to connect Facebook.'));
        resetFacebookConnect();
      }
    } catch (e) {
      console.error('[Facebook OAuth Dev] Connect failed', e);
      toast.error(getIntegrationErrorMessage(e, 'Failed to connect Facebook.'));
      resetFacebookConnect();
    }
  };

  const continueConnection = async (code) => {
    setConnecting(true);

    try {
      const result = await getFacebookAdAccounts(code, expectedAccountType);

      if (result?.account_type !== expectedAccountType) {
        facebookOAuthDevLog('Ignored callback for different account type', {
          expected: expectedAccountType,
          received: result?.account_type,
        });
        return;
      }

      if (expectedAccountType === 'posts') {
        toast.success(result?.message || 'Facebook account linked successfully.');
        fetchAdsAccountStatus?.();
        finishOAuthCallback();
        return;
      }

      const adAccountsList = Array.isArray(result?.data?.ad_accounts_list)
        ? result.data.ad_accounts_list
        : [];

      if (adAccountsList.length > 0) {
        setAdAccounts(adAccountsList);
        setShowAdsAccountsModal(true);
        clearOAuthCallbackParams(navigate);
        return;
      }

      toast.error('No Facebook ad accounts found for this profile.');
      finishOAuthCallback();
    } catch (e) {
      console.error('[Facebook OAuth Dev] Callback failed', e);
      toast.error(getIntegrationErrorMessage(e, 'Failed to connect Facebook.'));
      finishOAuthCallback();
    } finally {
      setConnecting(false);
    }
  };

  const initiateFacebookIntegration = () => {
    if (social_media_type === 'paid_social') {
      setConnecting(false);
      setConfirmAdsAccountModal(true);
      return;
    }

    handleConnect();
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const error = params.get('error');
    const errorDescription = params.get('error_description');
    const selectedPlatform = localStorage.getItem('selected_platform_to_connect');
    const storedAccountType = localStorage.getItem('account_type');

    facebookOAuthDevLog('OAuth callback check', {
      card: social_media_type,
      expected_account_type: expectedAccountType,
      has_code: Boolean(code),
      selected_platform_to_connect: selectedPlatform,
      stored_account_type: storedAccountType,
    });

    if (selectedPlatform !== 'facebook' || storedAccountType !== expectedAccountType) {
      return;
    }

    if (error) {
      toast.error(
        errorDescription ||
          `Facebook authorization failed. Add this URL in Meta Developer Console → Valid OAuth Redirect URIs: ${getOAuthRedirectUri()}`,
      );
      finishOAuthCallback();
      setConnecting(false);
      return;
    }

    if (code) {
      continueConnection(code);
    }
  }, []);

  return (
    <>
      <AdsCard
        disabled={false}
        handleConnect={initiateFacebookIntegration}
        accountDetails={accountDetails}
        isAccountLinked={isAccountLinked}
        btn_icon={<i className="bi bi-facebook px-1"></i>}
        btn_title="Connect with Facebook"
        connecting={connecting}
        platform={platform}
        platform_icon={facebook_ads_icon}
        description="Facebook is a leading social networking platform that enables users to connect, share, and communicate with others online."
        social_media_type={social_media_type}
        fetchAdsAccountStatus={fetchAdsAccountStatus}
      />

      {!connecting && (
        <CommonModal
          title={
            <div className="d-flex flex-column gap-1">
              <span className="fs-4 fw-bold">Your Facebook Ads Accounts</span>
              <small className="fs-8">Select an account to proceed with integration.</small>
            </div>
          }
          body={
            adAccounts.length > 0 && (
              <FacebookAdsAccountTable
                facebookAdAccounts={adAccounts}
                fetchAdsAccountStatus={fetchAdsAccountStatus}
                setShowAdsAccountsModal={setShowAdsAccountsModal}
                onLinked={finishOAuthCallback}
              />
            )
          }
          show={showAdsAccountsModal}
          setShow={setShowAdsAccountsModal}
        />
      )}

      <ConfirmModal
        show={confirmAdsAccountModal}
        onClose={resetFacebookConnect}
        title={<h5 className="fs-4 fw-medium">Connect Your Facebook Account</h5>}
        body={
          <p className="fs-6">
            To continue, please connect a <b>Facebook account</b> that has access to your{' '}
            <b>Facebook Pages</b> or <b>Ad Accounts</b>. This will allow us to fetch your campaign
            and page insights securely.
          </p>
        }
        closeBtnTitle="Cancel"
        submitBtnTitle="Continue with Facebook"
        onSubmit={handleConnect}
        loadingOnsubmit={connecting}
      />
    </>
  );
};

export default FacebookAdsIntegartion;
